import React, { useState } from "react";
import sortBy from "lodash/sortBy";
import { Button, Icon, ConfirmActionModal, TextField } from "ui/components";
import {
    PublisherSetting,
    SocialSetting,
} from "core/services/site/configValue";
import { usePublishContext } from "ui/context/PublishContext";
import usePermissions from "ui/hooks/usePermissions";
import { cx } from "utils/join-classnames";
import { SocialPlatform } from "core/models";
import styles from "./SocialSettingCard.module.scss";

interface Props {
    platform: SocialPlatform;
    accounts: PublisherSetting<SocialSetting>[];
    onConnectNew(platform: SocialPlatform): void;
    onReconnect(accountKey: string, platform: SocialPlatform): void;
    onDelete(accountKey: string): void;
    onChangeAccountName(
        accountKey: string,
        newAccountName: string,
    ): Promise<void>;
}

const SocialSettingCard: React.FC<Props> = ({
    platform,
    accounts = [],
    onConnectNew,
    onReconnect,
    onDelete,
    onChangeAccountName,
}: Props) => {
    const publishContext = usePublishContext();
    const userPermissions = usePermissions();
    const [collapsed, setCollapsed] = useState<boolean>(false);
    const [confirmDeleteModal, setConfirmDeleteModal] =
        useState<boolean>(false);
    const [deleteAccountKey, setDeleteAccountKey] = React.useState<string>("");
    const [modifiedAccounts, setModifiedAccounts] = React.useState<
        PublisherSetting<SocialSetting>[]
    >([]);
    const timerRef = React.useRef<NodeJS.Timeout | null>(null);

    React.useEffect(() => {
        setModifiedAccounts(accounts);
    }, [accounts]);

    const accountDetails =
        accounts.length > 0 ? `${accounts.length} Accounts` : "none";

    const checkActive = (key: string) => {
        switch (platform) {
            case "facebook":
                return publishContext.activeFacebookAccounts.some(
                    (account) => account.key === key,
                );
            case "twitter":
                return publishContext.activeTwitterAccounts.some(
                    (account) => account.key === key,
                );
            case "youtube":
                return publishContext.activeYoutubeAccounts.some(
                    (account) => account.key === key,
                );
            default:
                return null;
        }
    };

    const handleChangeAccountName = async (
        accountKey: string,
        newAccountName: string,
    ): Promise<void> => {
        const newModifiedAccounts = modifiedAccounts.map((account) => {
            if (account.key !== accountKey) {
                return account;
            }
            const copiedAccount = { ...account };
            copiedAccount.name = newAccountName;
            return copiedAccount;
        });
        setModifiedAccounts(newModifiedAccounts);
    };

    const handleEdit = (accountKey: string) => {
        const inputElement = document.getElementById(
            accountKey,
        ) as HTMLInputElement;
        inputElement.focus();
        inputElement.select();
    };

    React.useEffect(() => {
        if (JSON.stringify(accounts) === JSON.stringify(modifiedAccounts)) {
            return undefined;
        }
        const saveValues = async () => {
            await Promise.all(
                modifiedAccounts.map(async (modifiedAccount) => {
                    const changed =
                        modifiedAccount.name !==
                        accounts.find((acc) => acc.key === modifiedAccount.key)
                            ?.name;
                    if (!changed) {
                        return modifiedAccount;
                    }
                    await onChangeAccountName(
                        modifiedAccount.key,
                        modifiedAccount.name,
                    );
                    return modifiedAccount;
                }),
            );
            publishContext.reloadSocialAccounts();
            setConfirmDeleteModal(false);
        };
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            if (timerRef.current) {
                timerRef.current = null;
            }
        }
        timerRef.current = setTimeout(saveValues, 2000);
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [modifiedAccounts, accounts]);

    const sortedModifiedAccounts = sortBy(
        modifiedAccounts,
        (account) => account.key,
    );
    const accountsList = sortedModifiedAccounts.map((modifiedAccount) => {
        const changed =
            modifiedAccount.name !==
            accounts.find((acc) => acc.key === modifiedAccount.key)?.name;
        return (
            <div
                className={cx(
                    styles.card__account,
                    !checkActive(modifiedAccount.key)
                        ? styles.card__account__inactive
                        : null,
                )}
                key={modifiedAccount.key}
            >
                <div>
                    <div>
                        <TextField
                            id={modifiedAccount.key}
                            value={modifiedAccount.name}
                            theme="light"
                            defaultText="Account name"
                            onValueChanged={(value) =>
                                handleChangeAccountName(
                                    modifiedAccount.key,
                                    value,
                                )
                            }
                        />
                        <div className={styles.status}>
                            {changed && "Saving…"}
                        </div>
                    </div>
                    <ul>
                        {platform === "facebook" &&
                            publishContext.facebookPages
                                .filter(
                                    (page) =>
                                        page.publisherKey ===
                                        modifiedAccount.key,
                                )
                                .map((page) => (
                                    <li
                                        className={styles.card__account__page}
                                        key={page.id}
                                    >
                                        {page.name}
                                    </li>
                                ))}
                    </ul>
                </div>
                <Button
                    type="function"
                    theme="dark"
                    icon="edit"
                    tooltip="Edit account name"
                    onClick={() => handleEdit(modifiedAccount.key)}
                />
                <Button
                    type="function"
                    theme="dark"
                    icon="reload"
                    tooltip="reconnect"
                    onClick={() => onReconnect(modifiedAccount.key, platform)}
                />
                <Button
                    type="function"
                    theme="dark"
                    tooltip="delete"
                    onClick={() => {
                        setDeleteAccountKey(modifiedAccount.key);
                        setConfirmDeleteModal(true);
                    }}
                    icon="delete"
                />
            </div>
        );
    });

    return (
        <div className={styles.card}>
            <div className={styles.card__header}>
                <div
                    role="presentation"
                    className={styles.card__header__left}
                    onClick={() => setCollapsed(!collapsed)}
                >
                    <div className={styles.card__header__icon}>
                        <img
                            src={`/static/img/${platform}.svg`}
                            alt="platform"
                        />
                    </div>

                    <div className={styles.card__header__info}>
                        <div className={styles.card__header__info__title}>
                            {platform}
                            <Icon
                                name="dropdown-up"
                                className={
                                    collapsed
                                        ? styles.arrow
                                        : styles.arrow__down
                                }
                            />
                        </div>
                        <div className={styles.card__header__info__details}>
                            {accountDetails}
                        </div>
                    </div>
                </div>
                <div className={styles.card__header__right}>
                    <Button
                        theme="dark"
                        type="primary"
                        icon="plus"
                        onClick={async (e) => {
                            e.preventDefault();
                            onConnectNew(platform);
                        }}
                        disabled={!userPermissions.canConfigureShare}
                    >
                        Connect new
                    </Button>
                </div>
            </div>
            <div
                className={cx(
                    styles.card__body,
                    collapsed ? styles.card__body__collapsed : null,
                )}
            >
                {accounts.length <= 0 && userPermissions.canConfigureShare ? (
                    <span className={styles.card__body__label}>
                        No accounts connected yet{" "}
                    </span>
                ) : (
                    accountsList
                )}
                {!userPermissions.canConfigureShare && (
                    <span className={styles.card__body__label}>
                        Upgrade your plan to connect social accounts
                    </span>
                )}
            </div>
            {confirmDeleteModal && (
                <ConfirmActionModal
                    theme="light"
                    title="Really delete this?"
                    description="There is no way back from this."
                    confirmBtnText="Delete"
                    handleCancel={() => setConfirmDeleteModal(false)}
                    handleConfirm={() => {
                        onDelete(deleteAccountKey);
                        setConfirmDeleteModal(false);
                    }}
                />
            )}
        </div>
    );
};

export default SocialSettingCard;
